import * as React from 'react';
import type { TFunction } from 'i18next';
import { formatPrice } from '@ooh/ui/lib/media-unit-formatters';
import { maskRestrictedCell, type MediaUnitColumnDef } from './columns';
import { buildClientCostColumns } from './columns-client-cost';
import {
  renderMediaUnitAudience,
  renderMediaUnitFormat,
  renderMediaUnitGrwId,
} from '../../../lib/media-unit-field-renderers';

/**
 * Builds the 2 reverse-parity hidden-by-default columns (catalog-column-filter-parity)
 * — extracted so `buildExtendedColumns` stays within the file's
 * max-lines-per-function budget. Closes the filter→column direction of the
 * parity rule for filter dimensions that previously had no matching column:
 * formatCode, roadNumber. Both are sortable (CU-86cb1nqc0 follow-up): format
 * via the mediaFormats join (mediaFormats.code), roadNumber via the
 * primary-variant resolver (primaryVariantRoadNumberExpr, same "lowest
 * variant id" convention already used for mediaType). The `status` column
 * was removed entirely (CU-86cb1nq7h, client request — redundant with
 * detailed availability); it keeps its `MEDIA_UNIT_SORT_KEYS` entry
 * (presentation-only removal), but has no column or filter for any role
 * anymore.
 */
function buildReverseParityColumns(t: TFunction<'inventory', undefined>): MediaUnitColumnDef[] {
  return [
    {
      id: 'format',
      label: t('table.columns.format'),
      align: 'left',
      width: 140,
      sortable: true,
      sortKey: 'format',
      renderCell: (item) => renderMediaUnitFormat(item.formatCode, item.formatLabel),
    },
    {
      id: 'roadNumber',
      label: t('table.columns.roadNumber'),
      align: 'left',
      width: 120,
      sortable: true,
      sortKey: 'roadNumber',
      renderCell: (item) => <>{item.roadNumber ?? '\u2014'}</>,
    },
  ];
}

/**
 * Builds the 6 hidden-by-default internal-cost columns that get masked per row
 * via `maskRestrictedCell` when `costsWithheld` is set (field-visibility,
 * CU-86cbc3r8b) — extracted so `buildExtendedColumns` stays within the file's
 * max-lines-per-function budget.
 */
function buildMaskedCostColumns(
  t: TFunction<'inventory', undefined>,
  maskedExplanation: string,
): MediaUnitColumnDef[] {
  return [
    {
      id: 'rent',
      label: t('table.columns.rent'),
      align: 'right',
      width: 110,
      sortable: true,
      sortKey: 'rent',
      renderCell: (item) =>
        maskRestrictedCell(
          item.rent,
          item.rentWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.rent)}</span>,
        ),
    },
    {
      id: 'exposureCost',
      label: t('table.columns.exposureCost'),
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: 'exposureCost',
      renderCell: (item) =>
        maskRestrictedCell(
          item.exposureCost,
          item.costsWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.exposureCost)}</span>,
        ),
    },
    {
      id: 'additionalCosts',
      label: t('table.columns.additionalCosts'),
      align: 'right',
      width: 140,
      sortable: true,
      sortKey: 'additionalCosts',
      renderCell: (item) =>
        maskRestrictedCell(
          item.additionalCosts,
          item.costsWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.additionalCosts)}</span>,
        ),
    },
    {
      id: 'productionCost',
      label: t('table.columns.productionCost'),
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: 'productionCost',
      renderCell: (item) =>
        maskRestrictedCell(
          item.productionCost,
          item.costsWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.productionCost)}</span>,
        ),
    },
    {
      id: 'installationCost',
      label: t('table.columns.installationCost'),
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: 'installationCost',
      renderCell: (item) =>
        maskRestrictedCell(
          item.installationCost,
          item.costsWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.installationCost)}</span>,
        ),
    },
    {
      id: 'dismantlingCost',
      label: t('table.columns.dismantlingCost'),
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: 'dismantlingCost',
      renderCell: (item) =>
        maskRestrictedCell(
          item.dismantlingCost,
          item.costsWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{formatPrice(item.dismantlingCost)}</span>,
        ),
    },
  ];
}

/**
 * Builds the extended column defs (visible-by-default + hidden-by-default).
 * `maskedExplanation` is shown on a masked internal-cost cell (field-visibility,
 * CU-86cbc3r8b) — the 6 internal cost columns (`buildMaskedCostColumns`) stay
 * present for every caller and mask per row via `maskRestrictedCell`; the 7
 * client price columns (`buildClientCostColumns`) are never masked —
 * `cost.view` gates internal costs, never client prices.
 */
export function buildExtendedColumns(
  t: TFunction<'inventory', undefined>,
  maskedExplanation: string,
): MediaUnitColumnDef[] {
  return [
    // ── Visible by default — new columns ────────────────────────────────
    {
      id: 'agglomeration',
      label: t('table.columns.agglomeration'),
      align: 'left',
      width: 140,
      sortable: true,
      sortKey: 'agglomeration',
      renderCell: (item) => <>{item.agglomeration ?? '\u2014'}</>,
    },
    {
      id: 'county',
      label: t('table.columns.county'),
      align: 'left',
      width: 120,
      sortable: true,
      sortKey: 'county',
      renderCell: (item) => <>{item.county ?? '\u2014'}</>,
    },
    {
      id: 'audienceEstimate',
      label: t('table.columns.audienceEstimate'),
      align: 'right',
      width: 130,
      sortable: true,
      sortKey: 'audienceEstimate',
      renderCell: (item) => renderMediaUnitAudience(item.audienceEstimate),
    },
    // ── Hidden by default — non-cost columns ─────────────────────────────
    {
      id: 'grwId',
      label: t('table.columns.grwId'),
      align: 'left',
      width: 130,
      sortable: true,
      sortKey: 'grwId',
      renderCell: (item) => renderMediaUnitGrwId(item.grwId),
    },
    {
      id: 'material',
      label: t('table.columns.material'),
      align: 'left',
      width: 120,
      sortable: true,
      sortKey: 'material',
      renderCell: (item) => <>{item.material ?? '\u2014'}</>,
    },
    {
      id: 'notes',
      label: t('table.columns.notes'),
      align: 'left',
      width: 200,
      sortable: true,
      sortKey: 'notes',
      renderCell: (item) => <>{item.notes ?? '\u2014'}</>,
    },
    {
      id: 'locationDescription',
      label: t('table.columns.locationDescription'),
      align: 'left',
      width: 200,
      renderCell: (item) => <>{item.locationDescription ?? '\u2014'}</>,
    },
    {
      id: 'wfContactPerson',
      label: t('table.columns.wfContactPerson'),
      align: 'left',
      width: 200,
      renderCell: (item) => <>{item.wfContactPerson ?? '\u2014'}</>,
    },
    {
      id: 'multiformat',
      label: t('table.columns.multiformat'),
      align: 'left',
      width: 120,
      sortable: true,
      sortKey: 'multiformat',
      renderCell: (item) => <>{item.multiformat ?? '\u2014'}</>,
    },
    {
      id: 'tags',
      label: t('table.columns.tags'),
      align: 'left',
      width: 160,
      sortable: true,
      sortKey: 'tags',
      renderCell: (item) => (
        <>{item.tags.length > 0 ? item.tags.map((tag) => tag.label).join(', ') : '\u2014'}</>
      ),
    },
    // ── Hidden by default — cost columns (canViewCosts gated) ────────────
    ...buildMaskedCostColumns(t, maskedExplanation),
    ...buildClientCostColumns(t),
    // ─── Hidden by default — reverse-parity columns (catalog-column-filter-parity) ───
    ...buildReverseParityColumns(t),
  ];
}
